import { extractPayloadMessage } from '../utils/apiErrors'
import { buildQuotaToast } from '../utils/quotaToast'
import { willOfetchRetry } from '../utils/ofetchRetry'
import {
  appendTraceId,
  getTraceId,
  traceIdToastActions,
} from '../utils/traceContext'

const DEFAULT_TITLE = 'Request failed'
const DEFAULT_MESSAGE = 'Something went wrong. Please try again.'

const buildMessage = ({ message, status, statusText, defaultMessage }) => {
  const trimmedMessage = message?.trim()
  if (trimmedMessage) return trimmedMessage

  if (status) {
    const suffix = statusText ? ` ${statusText}` : ''
    return `HTTP ${status}${suffix}`
  }

  return defaultMessage || DEFAULT_MESSAGE
}

export default defineNuxtPlugin((nuxtApp) => {
  const toast = useToast()
  const toastDuration = Number(
    useRuntimeConfig().public.toastDurationMs || 5000
  )

  const getTranslation = (key, fallback, params) => {
    try {
      const i18n =
        nuxtApp?.$i18n || nuxtApp?.vueApp?.config?.globalProperties?.$i18n
      if (i18n?.t) {
        const translated = i18n.t(key, params)
        if (translated && translated !== key) return translated
      }
    } catch (error) {
      console.error('Error getting translation:', error)
    }
    return fallback
  }

  const showErrorToast = ({ status, statusText, message, traceId }) => {
    const title = getTranslation('notifications.api_error_title', DEFAULT_TITLE)
    const defaultMessage = getTranslation(
      'notifications.api_error_message',
      DEFAULT_MESSAGE
    )
    const traceLabel = getTranslation(
      'notifications.api_error_trace_id',
      'Trace ID'
    )
    const description = appendTraceId(
      buildMessage({ message, status, statusText, defaultMessage }),
      traceId,
      traceLabel
    )
    const copyLabel = getTranslation('notifications.api_error_copy', 'Copy')
    toast.add({
      title,
      description,
      color: 'error',
      duration: toastDuration,
      actions: traceIdToastActions(traceId, copyLabel),
    })
  }

  const showQuotaToast = ({ title, description }) => {
    toast.add({
      title,
      description,
      color: 'primary',
      icon: 'i-lucide-info',
      // The theme colors the icon with --ui-primary; the rest of the app uses the primary-600 shade.
      ui: { icon: 'text-primary-600' },
      duration: toastDuration,
    })
  }

  const apiFetch = $fetch.create({
    onResponseError({ response, options }) {
      if (options?.ignoreErrorToast) {
        return
      }
      // Skip attempts that ofetch will retry; toast only the final failure.
      if (willOfetchRetry(options, response?.status)) {
        return
      }
      // A used-up plan quota is informational, not an error.
      const quotaToast = buildQuotaToast(response, (key, params) =>
        getTranslation(key, null, params)
      )
      if (quotaToast) {
        showQuotaToast(quotaToast)
        return
      }
      showErrorToast({
        status: response?.status,
        statusText: response?.statusText,
        message: extractPayloadMessage(response?._data),
        traceId: getTraceId(response),
      })
    },
    onRequestError({ error, options }) {
      if (options?.ignoreErrorToast) {
        return
      }
      if (error?.name === 'AbortError') return
      showErrorToast({
        message: error?.message,
      })
    },
  })

  // Ensure Nuxt app + global $fetch use the instrumented instance.
  nuxtApp.$fetch = apiFetch
  nuxtApp.$fetchRaw = apiFetch.raw

  globalThis.$fetch = apiFetch
  globalThis.$fetchRaw = apiFetch.raw
})
